import React, { useEffect, useState } from 'react';
import { ProductSuggestionsContainer } from '../../ProductSuggestionsContainer';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { AlternativesProduct } from '../AlternativesProduct';
import { Slideout } from '../../Slideout/Slideout';
import { styles } from './AlternativesSlideout.css';
import { useProductActionsContext } from '../../context/ProductActionsProvider';
import { useAlternativesContext } from '../AlternativesContextProvider';
import { AddedToCartAlert } from '../../AddedToCartAlert';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner';
import { AlgoliaHitTransformer } from '../../Algolia/transformers/AlgoliaHitTransformer';
import { getProductPricing } from '../../ProductCard/utils/getProductPricing';

export const ANIMATION_DURATION = 3000;

export const AlternativesSlideout = () => {
  const isMobile = useBreakpoint('md', 'down');
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [shouldDisplayCartActions, setShouldDisplayCartActions] = useState(false);
  const {
    alternatives,
    isSlideoutOpen,
    currentProduct,
    isLoading,
    slideoutType,
    handleCloseSlideout,
    handleReviewCartClick,
    hanleContinueShoppingClick,
  } = useAlternativesContext();

  const { onAddToCart } = useProductActionsContext();

  const isAlternativesSlideout = slideoutType === 'alternatives';
  const title = isAlternativesSlideout ? 'Browse alternatives' : 'Similar items';

  const ecommerceExtraProps = {
    list: title,
    itemListSlot: 'slideout',
    itemListServiceConfig: 'SIMILAR',
  };
  const trackingDescriptor = {
    page: 'pdp',
    group: 'slideout',
    category: slideoutType,
    currency: getStoreCurrency(),
    ecommerceExtraProps,
  };

  const handleAddToCart = async (e: React.SyntheticEvent, hit: AlgoliaProductHit) => {
    if (!onAddToCart || isAddedToCart) return false;
    const isSuccess = await onAddToCart(e, hit, hit.variant_id, trackingDescriptor, 'alternatives');
    if (isSuccess) {
      setShouldDisplayCartActions(true);
      setIsAddedToCart(true);
    }
    return isSuccess;
  };

  useEffect(() => {
    if (!isAddedToCart) return;
    const timer = setTimeout(() => setIsAddedToCart(false), ANIMATION_DURATION);

    return () => clearTimeout(timer);
  }, [isAddedToCart]);

  const onClose = () => {
    handleCloseSlideout();
    setIsAddedToCart(false);
  };

  const {
    pricing,
    shouldShowWasPrice,
    perksPricing,
    isPerksMember,
    showPerksPrice,
    deliveryTag,
    shouldHidePerksExclusiveDealsFromNonMembers,
  } = AlgoliaHitTransformer.toProductCardProps(currentProduct || ({} as AlgoliaProductHit));

  const pricingValues = getProductPricing({
    pricing,
    shouldShowWasPrice,
    perksPricing,
    isPerksMember,
    showPerksPrice,
    shouldHidePerksExclusiveDealsFromNonMembers,
  });

  return (
    <Slideout
      isOpen={isSlideoutOpen}
      onClose={onClose}
      size={isMobile ? '100%' : '25rem'}
      anchor="right"
      dataTestId="alternatives-slideout"
      headerElement={!isMobile ? <h3 className={styles.heading({ isDesktop: !isMobile })}>{title}</h3> : null}
      bottomPadding={shouldDisplayCartActions ? 'extraPadding' : 'none'}
      zIndexBase={1002}
    >
      {currentProduct && isMobile && pricingValues ? (
        <AlternativesProduct
          {...currentProduct}
          price={pricingValues}
          deliveryTag={deliveryTag}
          productImage={currentProduct.product_image}
          onAddToCart={(e: React.SyntheticEvent) => handleAddToCart(e, currentProduct)}
        />
      ) : null}
      <div className={styles.container}>
        {isMobile ? <h3 className={styles.heading({ isMobile: isMobile })}>{title}</h3> : null}
        <div className={styles.content}>
          {isLoading ? <Spinner /> : null}
          {!isLoading && alternatives.length ? (
            <ProductSuggestionsContainer
              tileVariant={isAlternativesSlideout ? 'minified' : 'fullWidth'}
              suggestions={alternatives}
              onAddToCart={handleAddToCart}
              type={slideoutType}
              trackingProps={ecommerceExtraProps}
            />
          ) : null}
          {!isLoading && !alternatives.length ? (
            <p className={styles.noResultsHeader}>Looks like this item is one of a kind.</p>
          ) : null}
        </div>
      </div>
      {shouldDisplayCartActions ? (
        <div className={styles.addedToCartAlert}>
          <div className={styles.addedToCartAlertBackground} />
          <AddedToCartAlert
            onContinueShoppingClick={hanleContinueShoppingClick}
            onReviewCartClick={handleReviewCartClick}
            variant="minimalist"
            background="none"
            isAddedToCart={isAddedToCart}
            shouldPlayAddedAnimation
          />
        </div>
      ) : null}
    </Slideout>
  );
};
